import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore
} from "react";
import { config } from "@/config/environment";
import type { PWAState } from "@/types/core";
import {
  dismissServiceWorkerNeedRefresh,
  dismissServiceWorkerOfflineReady,
  getServerServiceWorkerStateSnapshot,
  getServiceWorkerStateSnapshot,
  getUpdateServiceWorker,
  subscribeServiceWorkerState
} from "@/pwa/sw-registration";

type LocalPwaState = {
  isInstalled: boolean;
  isOnline: boolean;
  isLoading: boolean;
};

export function usePWA() {
  const { needRefresh, offlineReady } = useSyncExternalStore(
    subscribeServiceWorkerState,
    getServiceWorkerStateSnapshot,
    getServerServiceWorkerStateSnapshot
  );

  const [localState, setLocalState] = useState<LocalPwaState>({
    isInstalled: false,
    isOnline: true,
    isLoading: true
  });

  useEffect(() => {
    let isMounted = true;

    const checkPWAStatus = () => {
      try {
        const isInstalled =
          window.matchMedia("(display-mode: standalone)").matches ||
          (window.navigator as Navigator & { standalone?: boolean })
            .standalone === true;
        const isOnline = navigator.onLine;

        if (isMounted) {
          setLocalState({
            isInstalled,
            isOnline,
            isLoading: false
          });
        }
      } catch (error) {
        if (config.debug.enabled) {
          console.error("PWA status check failed:", error);
        }
        if (isMounted) {
          setLocalState((prev) => ({ ...prev, isLoading: false }));
        }
      }
    };

    checkPWAStatus();

    const handleOnline = () =>
      setLocalState((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () =>
      setLocalState((prev) => ({ ...prev, isOnline: false }));

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      isMounted = false;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const updatePWA = useCallback(async () => {
    const run = getUpdateServiceWorker();
    if (!run) {
      return;
    }
    try {
      await run();
      if (config.debug.enabled) {
        console.log("PWA updated successfully");
      }
    } catch (error) {
      if (config.debug.enabled) {
        console.error("PWA update failed:", error);
      }
    }
  }, []);

  const clearPWACache = useCallback(async () => {
    if ("caches" in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );
        if (config.debug.enabled) {
          console.log("PWA cache cleared successfully");
        }
        window.location.reload();
      } catch (error) {
        if (config.debug.enabled) {
          console.error("Failed to clear PWA cache:", error);
        }
      }
    }
  }, []);

  const dismissUpdate = useCallback(() => {
    dismissServiceWorkerNeedRefresh();
  }, []);

  const dismissOfflineReady = useCallback(() => {
    dismissServiceWorkerOfflineReady();
  }, []);

  const merged: PWAState = {
    isInstalled: localState.isInstalled,
    isUpdateAvailable: needRefresh,
    isOnline: localState.isOnline,
    isLoading: localState.isLoading,
    needRefresh,
    offlineReady
  };

  return {
    ...merged,
    updatePWA,
    clearPWACache,
    dismissUpdate,
    dismissOfflineReady,
    workbox: {
      needRefresh,
      offlineReady,
      updateServiceWorker: getUpdateServiceWorker() ?? (async () => { })
    }
  };
}
